import { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';

const db = firebase.firestore();

/**
 * @typedef {Object} TaskData
 * @property {string} id
 * 
 * @typedef {Object} LocationData
 * @property {string} id
 * 
 * @typedef {Object} UserInfoData
 * @property {string} id
 */

/**
 * Custom hook for Firestore data management
 * Provides real-time listeners for tasks, locations, and users
 * Optimized: Single listener for tasks (real-time), fetch once for static data
 * 
 * @param {firebase.User | null} currentUser
 * @returns {{
 *   tasks: TaskData[],
 *   locations: LocationData[],
 *   users: UserInfoData[],
 *   loading: boolean
 * }}
 */
export function useFirestoreData(currentUser) {
  const [tasks, setTasks] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setTasks([]);
      setLocations([]);
      setUsers([]);
      setLoading(false);
      return;
    }

    // Real-time listeners for tasks (changes frequently)
    // NEW: Uses dedicated 'tasks' collection (task instances only)
    // Two scoped listeners instead of one capped `limit(1000)` feed:
    //  - open/overdue: small set, loaded in full (never silently truncated)
    //  - completed: newest first, capped (history beyond the cap is archive, not daily work)
    // Soft-delete (`active == true`) is enforced client-side so both queries
    // need only single-field / (status, completedAt) indexes.
    // Bounded + error-safe: unblocks loading on permission errors, avoids stale setState
    let cancelled = false;
    let openTasks = [];
    let completedTasks = [];
    let openReady = false;
    let completedReady = false;
    const mergeTasks = () => {
      if (cancelled) return;
      const byId = new Map();
      for (const t of [...openTasks, ...completedTasks]) byId.set(t.id, t);
      setTasks([...byId.values()].filter((t) => t.active === true));
      if (openReady && completedReady) setLoading(false);
    };
    const unsubscribeOpen = db.collection('tasks')
      .where('status', 'in', ['open', 'overdue'])
      .onSnapshot((snapshot) => {
        if (cancelled) return;
        const tasksData = [];
        snapshot.forEach((doc) => {
          tasksData.push({ id: doc.id, ...doc.data() });
        });
        openTasks = tasksData;
        openReady = true;
        mergeTasks();
      }, () => {
        if (cancelled) return;
        openReady = true;
        mergeTasks();
      });

    const unsubscribeCompleted = db.collection('tasks')
      .where('status', 'in', ['completed', 'completed-with-issues'])
      .orderBy('completedAt', 'desc')
      .limit(1000)
      .onSnapshot((snapshot) => {
        if (cancelled) return;
        const tasksData = [];
        snapshot.forEach((doc) => {
          tasksData.push({ id: doc.id, ...doc.data() });
        });
        completedTasks = tasksData;
        completedReady = true;
        mergeTasks();
      }, () => {
        if (cancelled) return;
        completedReady = true;
        mergeTasks();
      });

    // Static data: Fetch once, cache in memory (locations rarely change)
    const fetchLocations = async () => {
      try {
        const snapshot = await db.collection('locations')
          .where('active', '==', true)
          .get();
        if (cancelled) return;
        const locationsData = [];
        snapshot.forEach((doc) => {
          locationsData.push({ id: doc.id, ...doc.data() });
        });
        setLocations(locationsData);
      } catch {
        // Keep old list on transient failure
      }
    };

    // Static data: Fetch once, cache in memory (users rarely change)
    const fetchUsers = async () => {
      try {
        const snapshot = await db.collection('users')
          .where('active', '==', true)
          .get();
        if (cancelled) return;
        const usersData = [];
        snapshot.forEach((doc) => {
          usersData.push({ id: doc.id, ...doc.data() });
        });
        setUsers(usersData);
      } catch {
        // Keep old list on transient failure
      }
    };

    fetchLocations();
    fetchUsers();

    return () => {
      cancelled = true;
      unsubscribeOpen();
      unsubscribeCompleted();
    };
  }, [currentUser]);

  return {
    tasks,
    locations,
    users,
    loading
  };
}
